import ColorPicker from "../picker/ColorPicker";

const MAP_STYLES = [
    { value: 'standard', label: 'Standard', icon: '🗺️' },
    { value: 'hot', label: 'Humanitaire', icon: '🌍' },
    { value: 'toner', label: 'Noir & Blanc', icon: '⬛' },
    { value: 'terrain', label: 'Terrain', icon: '⛰️' },
    { value: 'watercolor', label: 'Aquarelle', icon: '🎨' },
    { value: 'dark', label: 'Sombre', icon: '🌙' },
    { value: 'satellite', label: 'Satellite', icon: '🛰️' },
    { value: 'streets', label: 'Rues', icon: '🛣️' },
];

const MARKER_ICONS = [
    { value: 'pin', label: 'Épingle', icon: '📍' },
    { value: 'restaurant', label: 'Restaurant', icon: '🍽️' },
    { value: 'cafe', label: 'Café', icon: '☕' },
    { value: 'bar', label: 'Bar', icon: '🍺' },
    { value: 'hotel', label: 'Hôtel', icon: '🏨' },
    { value: 'shop', label: 'Boutique', icon: '🛒' },
    { value: 'heart', label: 'Cœur', icon: '❤️' },
    { value: 'star', label: 'Étoile', icon: '⭐' },
    { value: 'home', label: 'Maison', icon: '🏠' },
    { value: 'building', label: 'Bâtiment', icon: '🏢' },
    { value: 'parking', label: 'Parking', icon: '🅿️' },
    { value: 'gas', label: 'Station', icon: '⛽' },
    { value: 'hospital', label: 'Hôpital', icon: '🏥' },
    { value: 'school', label: 'École', icon: '🎓' },
    { value: 'park', label: 'Parc', icon: '🌳' },
    { value: 'beach', label: 'Plage', icon: '🏖️' },
    { value: 'mountain', label: 'Montagne', icon: '⛰️' },
];

const INFO_POSITIONS = [
    { value: 'top-left', label: 'Haut-gauche', icon: '↖️' },
    { value: 'top-right', label: 'Haut-droite', icon: '↗️' },
    { value: 'bottom-left', label: 'Bas-gauche', icon: '↙️' },
    { value: 'bottom-right', label: 'Bas-droite', icon: '↘️' },
];

const SHADOW_OPTIONS = [
    { value: 'none', label: 'Aucune' },
    { value: 'light', label: 'Légère' },
    { value: 'medium', label: 'Moyenne' },
    { value: 'strong', label: 'Forte' },
];

export default function MapCellProperties({ cell, onChange }) {
    // Marqueurs additionnels
    const markers = Array.isArray(cell.markers) ? cell.markers : [];

    const addMarker = () => {
        const newMarkers = [...markers, { 
            latitude: cell.latitude || 48.8566, 
            longitude: cell.longitude || 2.3522, 
            icon: 'pin', 
            title: '',
            popup: '' 
        }];
        onChange(cell.cell_id, { markers: newMarkers, type: cell.type });
    };

    const removeMarker = (index) => {
        const newMarkers = markers.filter((_, i) => i !== index);
        onChange(cell.cell_id, { markers: newMarkers, type: cell.type });
    };

    const updateMarker = (index, field, value) => {
        const newMarkers = [...markers];
        newMarkers[index] = { ...newMarkers[index], [field]: value };
        onChange(cell.cell_id, { markers: newMarkers, type: cell.type });
    };

    return (
        <div className="property-section">
            <div className="property-section-title">📍 Localisation</div>
            
            {/* Coordonnées */}
            <div className="property-row">
                <div className="property-label">Latitude</div>
                <input 
                    className="property-input property-input-small" 
                    type="number" 
                    step="0.0001"
                    placeholder="48.8566"
                    value={cell.latitude ?? 48.8566} 
                    onChange={(e) => onChange(cell.cell_id, { latitude: parseFloat(e.target.value) || 0, type: cell.type })}
                />
            </div>
            <div className="property-row">
                <div className="property-label">Longitude</div>
                <input 
                    className="property-input property-input-small" 
                    type="number" 
                    step="0.0001"
                    placeholder="2.3522"
                    value={cell.longitude ?? 2.3522} 
                    onChange={(e) => onChange(cell.cell_id, { longitude: parseFloat(e.target.value) || 0, type: cell.type })}
                />
            </div>
            <div className="property-row">
                <div className="property-label">Zoom (1-19)</div>
                <input 
                    className="property-input property-input-small" 
                    type="range" 
                    min="1"
                    max="19"
                    value={cell.zoom ?? 14} 
                    onChange={(e) => onChange(cell.cell_id, { zoom: parseInt(e.target.value), type: cell.type })}
                />
                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                    {cell.zoom ?? 14}
                </span>
            </div>

            {/* Style de carte */}
            <div className="property-section-title">🎨 Style de la carte</div>
            <div className="property-row">
                <div className="property-label">Style</div>
                <select 
                    className="property-input property-input-small" 
                    value={cell.map_style || 'standard'} 
                    onChange={(e) => onChange(cell.cell_id, { map_style: e.target.value, type: cell.type })}
                >
                    {MAP_STYLES.map(style => (
                        <option key={style.value} value={style.value}>
                            {style.icon} {style.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className="property-row">
                <div className="property-label">Mode</div>
                <select 
                    className="property-input property-input-small" 
                    value={cell.map_mode || 'interactive'} 
                    onChange={(e) => onChange(cell.cell_id, { map_mode: e.target.value, type: cell.type })}
                >
                    <option value="interactive">🖱️ Interactive (Leaflet)</option>
                    <option value="iframe">🌐 IFrame (OSM)</option>
                    <option value="static">🖼️ Statique (Image)</option>
                </select>
            </div>

            {/* Marqueur principal */}
            <div className="property-section-title">📌 Marqueur principal</div>
            <div className="property-row">
                <div className="property-label">Afficher</div>
                <input 
                    className="property-input" 
                    type="checkbox" 
                    checked={cell.show_main_marker !== false} 
                    onChange={(e) => onChange(cell.cell_id, { show_main_marker: e.target.checked, type: cell.type })}
                />
            </div>
            {cell.show_main_marker !== false && (
                <>
                    <div className="property-row">
                        <div className="property-label">Icône</div>
                        <select 
                            className="property-input property-input-small" 
                            value={cell.main_marker_icon || 'pin'} 
                            onChange={(e) => onChange(cell.cell_id, { main_marker_icon: e.target.value, type: cell.type })}
                        >
                            {MARKER_ICONS.map(icon => (
                                <option key={icon.value} value={icon.value}>
                                    {icon.icon} {icon.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="property-row">
                        <div className="property-label">Titre du popup</div>
                        <input 
                            className="property-input property-input-small" 
                            type="text" 
                            placeholder="Notre restaurant"
                            value={cell.main_marker_title || ''} 
                            onChange={(e) => onChange(cell.cell_id, { main_marker_title: e.target.value, type: cell.type })}
                        />
                    </div>
                    <div className="property-row">
                        <div className="property-label">Contenu du popup</div>
                        <textarea 
                            className="property-input property-input-small" 
                            placeholder="Venez nous rendre visite !"
                            value={cell.main_marker_popup || ''} 
                            onChange={(e) => onChange(cell.cell_id, { main_marker_popup: e.target.value, type: cell.type })}
                            style={{ minHeight: '60px', resize: 'vertical' }}
                        />
                    </div>
                </>
            )}

            {/* Contrôles de la carte */}
            <div className="property-section-title">🎛️ Contrôles</div>
            <div className="property-row">
                <div className="property-label">Contrôles de zoom</div>
                <input 
                    className="property-input" 
                    type="checkbox" 
                    checked={cell.show_zoom_controls !== false} 
                    onChange={(e) => onChange(cell.cell_id, { show_zoom_controls: e.target.checked, type: cell.type })}
                />
            </div>
            <div className="property-row">
                <div className="property-label">Attribution</div>
                <input 
                    className="property-input" 
                    type="checkbox" 
                    checked={cell.show_attribution !== false} 
                    onChange={(e) => onChange(cell.cell_id, { show_attribution: e.target.checked, type: cell.type })}
                />
            </div>
            <div className="property-row">
                <div className="property-label">Glisser-déposer</div>
                <input 
                    className="property-input" 
                    type="checkbox" 
                    checked={cell.draggable !== false} 
                    onChange={(e) => onChange(cell.cell_id, { draggable: e.target.checked, type: cell.type })}
                />
            </div>
            <div className="property-row">
                <div className="property-label">Zoom molette</div>
                <input 
                    className="property-input" 
                    type="checkbox" 
                    checked={cell.scroll_wheel_zoom === true} 
                    onChange={(e) => onChange(cell.cell_id, { scroll_wheel_zoom: e.target.checked, type: cell.type })}
                />
            </div>

            {/* Géolocalisation utilisateur */}
            <div className="property-section-title">📍 Géolocalisation utilisateur</div>
            <div className="property-row">
                <div className="property-label">Afficher la position</div>
                <input 
                    className="property-input" 
                    type="checkbox" 
                    checked={cell.show_user_location === true} 
                    onChange={(e) => onChange(cell.cell_id, { show_user_location: e.target.checked, type: cell.type })}
                />
            </div>
            <div className="property-row">
                <div className="property-label">Bouton "Ma position"</div>
                <input 
                    className="property-input" 
                    type="checkbox" 
                    checked={cell.show_locate_button === true} 
                    onChange={(e) => onChange(cell.cell_id, { show_locate_button: e.target.checked, type: cell.type })}
                />
            </div>
            {(cell.show_user_location || cell.show_locate_button) && (
                <>
                    <div className="property-row">
                        <div className="property-label">Icône utilisateur</div>
                        <select 
                            className="property-input property-input-small" 
                            value={cell.user_marker_icon || '🔵'} 
                            onChange={(e) => onChange(cell.cell_id, { user_marker_icon: e.target.value, type: cell.type })}
                        >
                            <option value="🔵">🔵 Point bleu</option>
                            <option value="📍">📍 Épingle</option>
                            <option value="👤">👤 Personne</option>
                            <option value="🧑">🧑 Utilisateur</option>
                            <option value="📌">📌 Punaise</option>
                            <option value="⭐">⭐ Étoile</option>
                            <option value="🎯">🎯 Cible</option>
                        </select>
                    </div>
                    <div className="property-row">
                        <div className="property-label">Texte du bouton</div>
                        <input 
                            className="property-input property-input-small" 
                            type="text" 
                            placeholder="Ma position"
                            value={cell.locate_button_text || ''} 
                            onChange={(e) => onChange(cell.cell_id, { locate_button_text: e.target.value, type: cell.type })}
                        />
                    </div>
                </>
            )}

            {/* Overlay d'informations */}
            <div className="property-section-title">ℹ️ Overlay d'informations</div>
            <div className="property-row">
                <div className="property-label">Afficher l'overlay</div>
                <input 
                    className="property-input" 
                    type="checkbox" 
                    checked={cell.show_info_overlay === true} 
                    onChange={(e) => onChange(cell.cell_id, { show_info_overlay: e.target.checked, type: cell.type })}
                />
            </div>
            {cell.show_info_overlay && (
                <>
                    <div className="property-row">
                        <div className="property-label">Position</div>
                        <select 
                            className="property-input property-input-small" 
                            value={cell.info_position || 'bottom-left'} 
                            onChange={(e) => onChange(cell.cell_id, { info_position: e.target.value, type: cell.type })}
                        >
                            {INFO_POSITIONS.map(pos => (
                                <option key={pos.value} value={pos.value}>
                                    {pos.icon} {pos.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="property-row">
                        <div className="property-label">Titre</div>
                        <input 
                            className="property-input property-input-small" 
                            type="text" 
                            placeholder="Restaurant Le Gourmet"
                            value={cell.info_title || ''} 
                            onChange={(e) => onChange(cell.cell_id, { info_title: e.target.value, type: cell.type })}
                        />
                    </div>
                    <div className="property-row">
                        <div className="property-label">Adresse</div>
                        <textarea 
                            className="property-input property-input-small" 
                            placeholder="123 Rue de la Paix&#10;75001 Paris"
                            value={cell.info_address || ''} 
                            onChange={(e) => onChange(cell.cell_id, { info_address: e.target.value, type: cell.type })}
                            style={{ minHeight: '50px', resize: 'vertical' }}
                        />
                    </div>
                    <div className="property-row">
                        <div className="property-label">Téléphone</div>
                        <input 
                            className="property-input property-input-small" 
                            type="tel" 
                            placeholder="+33 1 23 45 67 89"
                            value={cell.info_phone || ''} 
                            onChange={(e) => onChange(cell.cell_id, { info_phone: e.target.value, type: cell.type })}
                        />
                    </div>
                    <div className="property-row">
                        <div className="property-label">Horaires</div>
                        <textarea 
                            className="property-input property-input-small" 
                            placeholder="Lun-Ven: 12h-14h, 19h-22h&#10;Sam-Dim: 12h-23h"
                            value={cell.info_hours || ''} 
                            onChange={(e) => onChange(cell.cell_id, { info_hours: e.target.value, type: cell.type })}
                            style={{ minHeight: '60px', resize: 'vertical' }}
                        />
                    </div>
                </>
            )}

            {/* Bouton itinéraire */}
            <div className="property-section-title">🧭 Bouton Itinéraire</div>
            <div className="property-row">
                <div className="property-label">Afficher</div>
                <input 
                    className="property-input" 
                    type="checkbox" 
                    checked={cell.show_directions_button !== false} 
                    onChange={(e) => onChange(cell.cell_id, { show_directions_button: e.target.checked, type: cell.type })}
                />
            </div>
            {cell.show_directions_button !== false && (
                <div className="property-row">
                    <div className="property-label">Texte du bouton</div>
                    <input 
                        className="property-input property-input-small" 
                        type="text" 
                        placeholder="Itinéraire"
                        value={cell.directions_button_text || ''} 
                        onChange={(e) => onChange(cell.cell_id, { directions_button_text: e.target.value, type: cell.type })}
                    />
                </div>
            )}

            {/* Marqueurs additionnels */}
            <div className="property-section-title">
                📍 Marqueurs additionnels
                <button 
                    onClick={addMarker}
                    style={{
                        marginLeft: '10px',
                        padding: '2px 8px',
                        fontSize: '12px',
                        background: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                    }}
                >
                    + Ajouter
                </button>
            </div>
            
            {markers.map((m, index) => (
                <div key={index} style={{ 
                    background: '#f5f5f5', 
                    padding: '10px', 
                    borderRadius: '8px', 
                    marginBottom: '10px' 
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
                            {MARKER_ICONS.find(ic => ic.value === m.icon)?.icon || '📍'} Marqueur {index + 1}
                        </span>
                        <button 
                            onClick={() => removeMarker(index)}
                            style={{
                                padding: '2px 6px',
                                fontSize: '10px',
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                            }}
                        >
                            ✕
                        </button>
                    </div>
                    <div className="property-row">
                        <div className="property-label">Latitude</div>
                        <input 
                            className="property-input property-input-small" 
                            type="number" 
                            step="0.0001"
                            value={m.latitude || 0} 
                            onChange={(e) => updateMarker(index, 'latitude', parseFloat(e.target.value) || 0)}
                        />
                    </div>
                    <div className="property-row">
                        <div className="property-label">Longitude</div>
                        <input 
                            className="property-input property-input-small" 
                            type="number" 
                            step="0.0001"
                            value={m.longitude || 0} 
                            onChange={(e) => updateMarker(index, 'longitude', parseFloat(e.target.value) || 0)}
                        />
                    </div>
                    <div className="property-row">
                        <div className="property-label">Icône</div>
                        <select 
                            className="property-input property-input-small" 
                            value={m.icon || 'pin'} 
                            onChange={(e) => updateMarker(index, 'icon', e.target.value)}
                        >
                            {MARKER_ICONS.map(icon => (
                                <option key={icon.value} value={icon.value}>
                                    {icon.icon} {icon.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="property-row">
                        <div className="property-label">Titre</div>
                        <input 
                            className="property-input property-input-small" 
                            type="text" 
                            placeholder="Parking gratuit"
                            value={m.title || ''} 
                            onChange={(e) => updateMarker(index, 'title', e.target.value)}
                        />
                    </div>
                    <div className="property-row">
                        <div className="property-label">Popup</div>
                        <input 
                            className="property-input property-input-small" 
                            type="text" 
                            placeholder="Description..."
                            value={m.popup || ''} 
                            onChange={(e) => updateMarker(index, 'popup', e.target.value)}
                        />
                    </div>
                </div>
            ))}

            {markers.length === 0 && (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '15px', 
                    color: '#999',
                    fontSize: '12px',
                    background: '#f9f9f9',
                    borderRadius: '8px',
                }}>
                    Ajoutez des marqueurs pour indiquer<br/>
                    des points d'intérêt (parking, entrée, etc.)
                </div>
            )}

            {/* Style */}
            <div className="property-section-title">🎨 Apparence</div>
            <div className="property-row">
                <div className="property-label">Arrondi des coins</div>
                <input 
                    className="property-input property-input-small" 
                    type="range" 
                    min="0"
                    max="50"
                    value={cell.styles_properties?.border_radius ?? 16} 
                    onChange={(e) => onChange(cell.cell_id, { styles_properties: { ...cell.styles_properties, border_radius: parseInt(e.target.value) }, type: cell.type })}
                />
                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                    {cell.styles_properties?.border_radius ?? 16}px
                </span>
            </div>
            <div className="property-row">
                <div className="property-label">Épaisseur bordure</div>
                <input 
                    className="property-input property-input-small" 
                    type="range" 
                    min="0"
                    max="10"
                    value={cell.styles_properties?.border_width ?? 0} 
                    onChange={(e) => onChange(cell.cell_id, { styles_properties: { ...cell.styles_properties, border_width: parseInt(e.target.value) }, type: cell.type })}
                />
                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                    {cell.styles_properties?.border_width ?? 0}px
                </span>
            </div>
            <div className="property-row">
                <div className="property-label">Ombre</div>
                <select 
                    className="property-input property-input-small" 
                    value={cell.styles_properties?.shadow_intensity || 'medium'} 
                    onChange={(e) => onChange(cell.cell_id, { styles_properties: { ...cell.styles_properties, shadow_intensity: e.target.value }, type: cell.type })}
                >
                    {SHADOW_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Couleurs */}
            <div className="property-section-title">🎨 Couleurs</div>
            <div className="property-row">
                <div className="property-label">Couleur principale</div>
                <ColorPicker 
                    value={cell.styles_properties?.primary_color || '#3B82F6'} 
                    onChange={(color) => onChange(cell.cell_id, { styles_properties: { ...cell.styles_properties, primary_color: color }, type: cell.type })}
                />
            </div>
            <div className="property-row">
                <div className="property-label">Couleur secondaire</div>
                <ColorPicker 
                    value={cell.styles_properties?.secondary_color || '#1E40AF'} 
                    onChange={(color) => onChange(cell.cell_id, { styles_properties: { ...cell.styles_properties, secondary_color: color }, type: cell.type })}
                />
            </div>
            <div className="property-row">
                <div className="property-label">Couleur de bordure</div>
                <ColorPicker 
                    value={cell.styles_properties?.border_color || '#E5E7EB'} 
                    onChange={(color) => onChange(cell.cell_id, { styles_properties: { ...cell.styles_properties, border_color: color }, type: cell.type })}
                />
            </div>
            <div className="property-row">
                <div className="property-label">Couleur du texte</div>
                <ColorPicker 
                    value={cell.styles_properties?.text_color || '#1F2937'} 
                    onChange={(color) => onChange(cell.cell_id, { styles_properties: { ...cell.styles_properties, text_color: color }, type: cell.type })}
                />
            </div>
        </div>
    );
}

